import { CaretDown, CaretLeft, CaretRight } from "@phosphor-icons/react";
import styled from "@emotion/styled";
import { twRing } from "@/lib/emotionTw";
import { Button } from "@/components/primitives/Button";
import { Dropdown } from "@/components/ui/overlays/Dropdown";
import type { TablePaginationProps } from "./DataTable.types";

/* Exact compiled output of the pre-migration Tailwind utilities, in the same
 * class-list emission order. Verified against the frozen baseline. */

const TablePaginationRoot = styled("nav", {
  label: "AgenticTablePagination-root"
})({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
  gap: ".75rem",
  borderTopWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  paddingLeft: "1.25rem",
  paddingRight: "1.25rem",
  paddingTop: "7px",
  paddingBottom: "7px"
});

const TablePaginationInfo = styled("div", {
  label: "AgenticTablePagination-info"
})({
  display: "flex",
  alignItems: "center",
  gap: ".5rem",
  fontSize: ".75rem",
  lineHeight: "1rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const TablePaginationSelect = styled("select", {
  label: "AgenticTablePagination-select"
})({
  height: "26px",
  minWidth: "100px",
  borderRadius: "var(--bfrs-radius-md)",
  borderWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  paddingLeft: ".5rem",
  paddingRight: ".5rem",
  fontSize: ".75rem",
  lineHeight: "1rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  "&:focus-visible": {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border-focus) / var(--tw-border-opacity, 1))",
    ...twRing("2px", "hsl(var(--bfrs-color-border-focus) / .2)")
  }
});

const TablePaginationEllipsis = styled("span", {
  label: "AgenticTablePagination-ellipsis"
})({
  paddingLeft: ".5rem",
  paddingRight: ".5rem",
  fontSize: ".75rem",
  lineHeight: "1rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const TablePaginationPageButton = styled("button", {
  label: "AgenticTablePagination-pageButton",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { current: boolean } }>(({ ownerState }) => [
  {
    height: "26px",
    minWidth: "26px",
    borderRadius: "var(--bfrs-radius-md)",
    paddingLeft: ".5rem",
    paddingRight: ".5rem",
    fontSize: ".75rem",
    lineHeight: "1rem",
    fontWeight: 600,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    transitionProperty:
      "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s",
    "&:hover": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))"
    },
    "&:focus-visible": {
      ...twRing("2px", "hsl(var(--bfrs-color-border-focus) / var(--tw-ring-opacity, 1))"),
      "--tw-ring-opacity": "1"
    }
  },
  ownerState.current && {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-text-secondary) / var(--tw-bg-opacity, 1))",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary-contrast) / var(--tw-text-opacity, 1))",
    "&:hover": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-text-secondary) / var(--tw-bg-opacity, 1))"
    }
  }
]);

const TablePaginationNav = styled("div", {
  label: "AgenticTablePagination-nav"
})({
  display: "flex",
  alignItems: "center",
  gap: ".25rem"
});

function buildPageNumbers(current: number, total: number): (number | "...")[] {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);
  if (current <= 3) return [1, 2, 3, "...", total];
  if (current >= total - 2) return [1, "...", total - 2, total - 1, total];
  const pages: (number | "...")[] = [1];
  if (current > 3) pages.push("...");
  for (let page = Math.max(2, current - 1); page <= Math.min(total - 1, current + 1); page += 1) {
    pages.push(page);
  }
  if (current < total - 2) pages.push("...");
  pages.push(total);
  return pages;
}

function formatPageSizeLabel(option: number, itemLabel: TablePaginationProps["itemLabel"]) {
  return typeof itemLabel === "string" || typeof itemLabel === "number" ? `${option} ${itemLabel}` : String(option);
}

export function TablePagination({
  page,
  totalPages,
  variant = "indexed",
  pageSize,
  totalItems,
  pageSizeOptions = [10, 15, 20, 25, 50],
  onPageChange,
  onPageSizeChange,
  itemLabel = "items",
  itemsPerPageLabel = "Items per page:",
  className,
  pageSizeControl = "select",
  showPageNavigation = true
}: TablePaginationProps) {
  const normalizedTotalPages = Math.max(1, totalPages);
  const pages = buildPageNumbers(page, normalizedTotalPages);
  const showIndexedPages = showPageNavigation && variant === "indexed";

  return (
    <TablePaginationRoot className={className} aria-label="Table pagination">
      <TablePaginationInfo>
        {onPageSizeChange && pageSize && (
          <>
            <span>{itemsPerPageLabel}</span>
            {pageSizeControl === "menu" ? (
              <Dropdown
                align="start"
                size="sm"
                trigger={
                  <Button variant="secondary" size="xs" rightIcon={<CaretDown size={10} />}>
                    {pageSize} {itemLabel}
                  </Button>
                }
                items={pageSizeOptions.map((option) => ({
                  label: `${option} ${itemLabel}`,
                  checked: option === pageSize,
                  onSelect: () => onPageSizeChange(option)
                }))}
              />
            ) : (
              <TablePaginationSelect value={pageSize} onChange={(event) => onPageSizeChange(Number(event.target.value))}>
                {pageSizeOptions.map((option) => (
                  <option key={option} value={option}>
                    {formatPageSizeLabel(option, itemLabel)}
                  </option>
                ))}
              </TablePaginationSelect>
            )}
          </>
        )}
        {pageSizeControl !== "menu" && typeof totalItems === "number" && <span>{totalItems} {itemLabel}</span>}
      </TablePaginationInfo>
      {showPageNavigation && (
        <TablePaginationNav>
          <Button aria-label="Previous page" variant="ghost" size="xs" disabled={page <= 1} leftIcon={<CaretLeft size={12} />} onClick={() => onPageChange(Math.max(1, page - 1))}>
            PREV
          </Button>
          {showIndexedPages && pages.map((entry, index) =>
            entry === "..." ? (
              <TablePaginationEllipsis key={`ellipsis-${index}`}>...</TablePaginationEllipsis>
            ) : (
              <TablePaginationPageButton
                key={entry}
                type="button"
                aria-current={entry === page ? "page" : undefined}
                aria-label={`Page ${entry}`}
                ownerState={{ current: entry === page }}
                onClick={() => onPageChange(entry)}
              >
                {entry}
              </TablePaginationPageButton>
            )
          )}
          <Button aria-label="Next page" variant="ghost" size="xs" disabled={page >= normalizedTotalPages} rightIcon={<CaretRight size={12} />} onClick={() => onPageChange(Math.min(normalizedTotalPages, page + 1))}>
            NEXT
          </Button>
        </TablePaginationNav>
      )}
    </TablePaginationRoot>
  );
}
